import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { Company } from './models';

const COMPANY_KEY = 'mkt.company';

function readCompany(): number | null {
  try {
    const value = localStorage.getItem(COMPANY_KEY);
    return value ? Number(value) : null;
  } catch {
    return null;
  }
}

/**
 * Empresa sobre la que se trabaja. Supervisor/usuario: la suya.
 * Admin: la que elija en la toolbar (se envía como X-Company-Id).
 */
@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);

  private readonly selectedId = signal<number | null>(readCompany());
  readonly companies = signal<Company[]>([]);

  /** Id que se envía en X-Company-Id (solo admin). */
  readonly adminCompanyId = computed(() => (this.auth.isAdmin() ? this.selectedId() : null));

  readonly companyId = computed(() => (this.auth.isAdmin() ? this.selectedId() : (this.auth.user()?.company?.id ?? null)));

  readonly companyName = computed(() => {
    if (!this.auth.isAdmin()) return this.auth.user()?.company?.name ?? null;
    return this.companies().find((c) => c.id === this.selectedId())?.name ?? null;
  });

  readonly ready = computed(() => this.companyId() !== null);

  constructor() {
    effect(() => {
      if (this.auth.isAdmin()) this.loadCompanies();
      else this.companies.set([]);
    });
  }

  select(id: number | null): void {
    this.selectedId.set(id);
    try {
      if (id) localStorage.setItem(COMPANY_KEY, String(id));
      else localStorage.removeItem(COMPANY_KEY);
    } catch {
      /* ignorar */
    }
  }

  loadCompanies(): void {
    this.api.companies({ per_page: 100 }).subscribe({
      next: (res) => {
        this.companies.set(res.data);
        const selected = this.selectedId();
        if (selected && !res.data.some((c) => c.id === selected)) this.select(null);
      },
      error: () => this.companies.set([]),
    });
  }
}
